import type { GrupoNavegacao } from './navegacao.js';
import { MARCA_PG } from './marca.js';

/**
 * Home pública da PG Custom — a marca no topo da sidebar linka para cá (mesmo comportamento do
 * `<a href="/">` do painel antigo, `admin/dashboard.html`). Domínio canônico com `www`
 * (`SITE_BASE_URL` do backend); abre em nova aba para não descartar a sessão do ERP.
 */
const SITE_URL = 'https://www.pgcustom.com.br/';

/**
 * Conjunto de ícones stroke da navegação — mesmo estilo visual (24x24,
 * stroke-width 2, sem preenchimento) já usado no sino de notificações
 * de `erp-header` e nos ícones de `pg-kpi-card`. Vive aqui (não no
 * Design System) porque as chaves são específicas dos módulos do ERP
 * (`CODING_STANDARDS.md`, seção 15: ícone de domínio não é componente
 * genérico) — puramente decorativo, nunca implica permissão real (a
 * autorização de cada rota continua sendo decidida por `permissoesX`).
 */
const ICONES_NAVEGACAO: Record<string, string> = {
  dashboard: '<rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" />',
  usuarios: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />',
  usuario: '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />',
  carrinho: '<circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />',
  producao: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 15l2 2 4-4" />',
  estoque: '<path d="M21 8V21H3V8" /><path d="M1 3h22v5H1z" /><path d="M10 12h4" />',
  envios: '<rect x="1" y="3" width="15" height="13" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />',
  dinheiro: '<path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />',
  suporte: '<circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" /><path d="M4.9 4.9l4.24 4.24M14.86 14.86l4.24 4.24M14.86 9.14l4.24-4.24M4.9 19.1l4.24-4.24" />',
  garantias: '<path d="M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11z" /><path d="M9 12l2 2 4-4" />',
  marketing: '<path d="M3 11l18-7-7 18-2-8-9-3z" />',
  parceiros: '<rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />',
  cms: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h8M8 9h2" />',
  galeria: '<rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" />',
  automacoes: '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />',
  permissoes: '<rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />',
  configuracoes: '<circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />',
  auditoria: '<path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />',
  bonus: '<circle cx="12" cy="8" r="7" /><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />',
};

function svgIcone(chave: string): string {
  const pathInner = ICONES_NAVEGACAO[chave];
  if (!pathInner) return '';
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${pathInner}</svg>`;
}

/**
 * `<erp-sidebar>` — navegação principal do ERP. Diferente dos
 * componentes de `packages/design-system`, este elemento CONHECE a
 * estrutura de módulos do produto (rótulos, caminhos, ícones) — por
 * isso vive em `apps/web`, nunca no Design System (`CODING_STANDARDS.md`,
 * seção 15: componente de design system nunca conhece entidade/domínio).
 *
 * Recebe `grupos` e `ativo` via propriedade JS (dado estruturado, mesmo
 * padrão de `pg-table.columns`/`.rows`). Emite `erp-navegar` com
 * `detail: { caminho }` ao clicar um item — nunca chama o roteador
 * diretamente, quem decide o que fazer com a navegação é quem monta o
 * shell (`main.ts`).
 */
export class ErpSidebar extends HTMLElement {
  private readonly root: ShadowRoot;
  private _grupos: GrupoNavegacao[] = [];
  private _ativo = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set grupos(valor: GrupoNavegacao[]) {
    this._grupos = valor;
    this.render();
  }

  set ativo(caminho: string) {
    this._ativo = caminho;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private render(): void {
    const grupos = this._grupos
      .map(
        (grupo) => `
          <div class="grupo">
            ${grupo.titulo ? `<div class="titulo-grupo">${escaparHtml(grupo.titulo)}</div>` : ''}
            <ul>
              ${grupo.itens
                .map(
                  (item) => `
                    <li>
                      <button
                        type="button"
                        class="item ${item.caminho === this._ativo ? 'ativo' : ''}"
                        data-caminho="${escaparHtml(item.caminho)}"
                      >
                        <span class="item-icone">${svgIcone(item.icone)}</span>
                        <span class="item-rotulo">${escaparHtml(item.rotulo)}</span>
                      </button>
                    </li>`
                )
                .join('')}
            </ul>
          </div>`
      )
      .join('');

    this.root.innerHTML = `
      <style>
        :host {
          display: block;
          height: 100%;
          background: var(--pg-color-bg-elevado, #1a1d21);
          border-right: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          font-family: var(--pg-font-familia-base, system-ui);
          overflow-y: auto;
        }
        .marca {
          display: flex;
          align-items: center;
          gap: var(--pg-space-3, 12px);
          padding: var(--pg-space-5, 24px) var(--pg-space-4, 16px);
          border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          margin-bottom: var(--pg-space-4, 16px);
          text-decoration: none;
          color: inherit;
          transition: background-color 0.15s ease;
        }
        a.marca:hover { background: var(--pg-color-bg-hover, #22262b); }
        a.marca:focus-visible {
          outline: none;
          box-shadow: var(--pg-shadow-foco, 0 0 0 3px rgba(47,140,255,0.35));
        }
        .marca-emblema {
          width: 34px;
          height: 34px;
          flex-shrink: 0;
          object-fit: contain;
          /* Halo dourado sutil — mesmo espírito do protótipo (drop-shadow leve). */
          filter: drop-shadow(0 0 6px rgba(201, 162, 75, 0.18));
        }
        .marca-texto {
          display: flex;
          flex-direction: column;
          line-height: 1.15;
          min-width: 0;
        }
        .marca-nome {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-base, 1rem);
          font-weight: var(--pg-font-weight-bold, 700);
          color: var(--pg-color-texto-primario, #f2f3f5);
          letter-spacing: 0.01em;
        }
        .marca-nome span { color: var(--pg-color-dourado, #c9a24b); }
        .marca-subtitulo {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--pg-color-texto-desabilitado, #5c626b);
        }
        nav {
          display: flex;
          flex-direction: column;
          gap: var(--pg-space-5, 24px);
          padding: 0 var(--pg-space-3, 12px) var(--pg-space-5, 24px);
        }
        .titulo-grupo {
          padding: 0 var(--pg-space-3, 12px);
          margin-bottom: var(--pg-space-2, 8px);
          font-size: var(--pg-font-size-xs, 0.75rem);
          font-weight: var(--pg-font-weight-semibold, 600);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--pg-color-texto-secundario, #9aa1ab);
        }
        ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
        .item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: var(--pg-space-3, 12px);
          text-align: left;
          background: none;
          border: none;
          border-left: 2px solid transparent;
          border-radius: var(--pg-radius-md, 10px);
          padding: 10px var(--pg-space-3, 12px);
          font-size: var(--pg-font-size-sm, 0.875rem);
          font-weight: var(--pg-font-weight-medio, 500);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          cursor: pointer;
          transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }
        .item-icone { display: flex; flex-shrink: 0; width: 18px; height: 18px; opacity: 0.85; }
        .item-icone svg { width: 100%; height: 100%; }
        .item-rotulo { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .item:hover { background: var(--pg-color-bg-hover, #22262b); color: var(--pg-color-texto-primario, #f2f3f5); }
        .item:focus-visible {
          outline: none;
          box-shadow: var(--pg-shadow-foco, 0 0 0 3px rgba(47,140,255,0.35));
        }
        .item.ativo {
          background: rgba(201, 162, 75, 0.1);
          color: var(--pg-color-texto-primario, #f2f3f5);
          border-left-color: var(--pg-color-dourado, #c9a24b);
        }
        .item.ativo .item-icone { color: var(--pg-color-dourado, #c9a24b); opacity: 1; }
        /* Trilho só-ícone — acompanha o breakpoint de erp-shell.ts
           (grid-template-columns cai para 64px). Título/rótulos escondidos
           via CSS, nunca removidos do DOM (leitor de tela ainda os alcança). */
        @media (max-width: 900px) {
          .marca { justify-content: center; padding: var(--pg-space-4, 16px) var(--pg-space-2, 8px); }
          .marca-texto { display: none; }
          .titulo-grupo { display: none; }
          .item { justify-content: center; padding: 10px 6px; }
          .item-rotulo { display: none; }
        }
      </style>
      <a
        class="marca"
        href="${SITE_URL}"
        target="_blank"
        rel="noopener noreferrer"
        title="Abrir o site da PG Custom em nova aba"
      >
        <img class="marca-emblema" src="${MARCA_PG.emblemaOuro}" alt="" aria-hidden="true" />
        <span class="marca-texto">
          <span class="marca-nome">PG <span>Custom</span></span>
          <span class="marca-subtitulo">ERP</span>
        </span>
      </a>
      <nav part="nav">${grupos}</nav>
    `;

    this.root.querySelectorAll<HTMLButtonElement>('.item').forEach((botao) => {
      botao.addEventListener('click', () => {
        const caminho = botao.dataset.caminho;
        if (!caminho) return;
        this.dispatchEvent(
          new CustomEvent('erp-navegar', { bubbles: true, composed: true, detail: { caminho } })
        );
      });
    });
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-sidebar', ErpSidebar);

declare global {
  interface HTMLElementTagNameMap {
    'erp-sidebar': ErpSidebar;
  }
}
